import { useState } from 'react';
import { Link, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router';
import AlertBanner from '@common/feedback/AlertBanner';
import VendorTermsModal from '@common/vendor/VendorTermsModal';
import { useGetProfileQuery, useGetVendorProfileQuery } from '@/redux/api/userApi';
import { useGetVendorSetupStatusQuery } from '@/redux/api/vendorApi';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { askVendorTerms, closeVendorTerms } from '@/redux/slices/vendorTerms';
import { useStoreVendorTerms } from '@/hooks/useStoreVendorTerms';
import { useStripeOnboarding } from '@/hooks/useStripeOnboarding';
import { PATHS } from '@/navigation/paths';
import { sanitizeReturnTo } from '@/navigation/utils/returnTo';

/**
 * Persistent banner shown on vendor dashboard pages when required setup is incomplete.
 * Shows which items are missing (rental agreement, pickup instructions, Stripe account)
 * with links to the relevant pages. Returns null for non-vendors or when setup is complete.
 *
 * The Stripe item (#1158) shows until Stripe has charges and payouts on,
 * not merely until an account exists (it exists from signup), and it is a
 * button, not a link: it starts Stripe's onboarding from this very page,
 * and Stripe sends the vendor back here when they finish or leave. When
 * Stripe did not answer (stripeReady null) the item says nothing rather
 * than either thing.
 *
 * While a new store awaits the admin's approve (status CREATED; GG-1063,
 * a store goes live only after admin approval) a second banner says so:
 * renters cannot see the store yet, and the owner hears by email once it
 * is approved. Setup can proceed in the meantime, so both banners show.
 *
 * While the store has not accepted GearGhost's vendor terms in force
 * (docs/plans/taxes.md, Decision 47: until then it cannot publish a listing
 * or accept an order) a third banner asks, and opens the terms right on
 * the page the vendor is on. It tells the vendor why before they click
 * (Tyler, 2026-10-06), and it is the one ask on every dashboard page,
 * Account Standing included, whose Vendor terms row only records the
 * standing (Tyler, 2026-10-10: "There should at most be one banner asking
 * for a read and accept of the new terms"). The words are Tyler's.
 *
 * The window is the banner's, on whatever page the vendor is, and opens
 * three ways: its own Read and accept; an accept-order the backend refuses
 * for want of the terms, which opens it right there
 * (useOpenVendorTermsOnRefusal; Tyler, 2026-10-10, as the app's sheet opens
 * in place); and a store page's loader (storeLoader), which sends such a
 * store to Account Standing with `?returnTo=`, the page it was on. Arriving
 * that way the terms open without a click, and accepting goes back to that
 * page.
 */
export function VendorSetupBanner() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { key: arrival } = useLocation();
  const [searchParams] = useSearchParams();
  // A store sent to Account Standing to accept carries the page to go back
  // to (storeLoader). Only there does `returnTo` mean that; elsewhere it
  // belongs to other flows.
  const onAccountStanding = useMatch(PATHS.dashboard.accountStanding) !== null;
  const returnTo = onAccountStanding ? sanitizeReturnTo(searchParams.get('returnTo')) : null;

  const { data: user } = useGetProfileQuery();
  const isVendor = user?.role === 'vendor';

  const { data: status } = useGetVendorSetupStatusQuery(undefined, {
    skip: !isVendor,
  });
  const { data: vendor } = useGetVendorProfileQuery(undefined, { skip: !isVendor });
  const { start: startStripeOnboarding, loading: startingStripe } = useStripeOnboarding();
  const {
    standing: termsStanding,
    accepting: acceptingTerms,
    reportSeen,
    accept: acceptTerms,
  } = useStoreVendorTerms({ skip: !isVendor });
  // Asked for by the banner's button or by a refused accept-order (the
  // vendorTerms slice).
  const asked = useAppSelector((state) => state.vendorTerms.asked);
  // The arrival whose window the vendor closed. The banner stays mounted as
  // the vendor moves between dashboard pages (DashboardLayout), so a store
  // sent here is told apart by the router's key for that arrival: closing
  // the terms stands for that arrival, and the next one opens them again.
  const [closedArrival, setClosedArrival] = useState<string | null>(null);

  if (!isVendor) return null;

  // Each banner waits on its own request and no other: the setup items on
  // the setup status, the terms on the standing. A setup status that has not
  // answered, or failed, keeps the terms ask from nothing, since the terms
  // are why publish and accept-order refuse.
  const underReview = vendor?.status === 'CREATED';
  // The server's answer; while it has not come, the banner waits rather than asks.
  const termsNeeded = termsStanding !== undefined && !termsStanding.accepted;

  type SetupItem = { label: string; path: string } | { label: string; onClick: () => void };
  const items: SetupItem[] = [];
  if (status !== undefined) {
    // The documents live on the Store Documents page (profile plan item 93).
    if (!status.hasRentalAgreement) {
      items.push({ label: 'Rental Agreement', path: `${PATHS.dashboard.store.documents}?tab=rental` });
    }
    if (!status.hasPickupInstructions) {
      items.push({ label: 'Pick-Up Instructions', path: `${PATHS.dashboard.store.documents}?tab=pickup` });
    }
    // While Stripe verifies what the vendor sent, there is nothing for them
    // to add, so the item stays out (Tyler, 2026-10-10); the Stripe Connect
    // page says Stripe is verifying.
    if (status.stripeReady === false && status.stripeVerifying !== true) {
      items.push({ label: 'Stripe Account', onClick: startStripeOnboarding });
    }
  }

  if (items.length === 0 && !underReview && !termsNeeded) return null;

  // Sent here to accept, the terms open without a click: reading them is why the vendor is here.
  const readingTerms = asked || (returnTo !== null && closedArrival !== arrival);
  const closeTerms = () => {
    dispatch(closeVendorTerms());
    setClosedArrival(arrival);
  };
  // Accepting goes back to the page that sent the vendor, when one did.
  const acceptTermsAndReturn = async (version: number) => {
    if ((await acceptTerms(version)) && returnTo !== null) navigate(returnTo, { replace: true });
  };

  return (
    <>
      {underReview && (
        <AlertBanner variant="warning" title="Your store is under review" testID="store-under-review">
          We review every new store before it goes live. Renters cannot see your store yet;
          we will email you as soon as it is approved.
        </AlertBanner>
      )}
      {termsNeeded && (
        <>
          <AlertBanner
            variant="warning"
            title="New Terms of Service"
            testID="vendor-terms-needed"
            actionLabel="Read and accept"
            onAction={() => dispatch(askVendorTerms())}
            actionLoading={acceptingTerms}
          >
            {"Until you accept the new Terms of Service, you can't publish listings or accept orders."}
          </AlertBanner>
          {/* The window closes itself on I Accept; the banner holds its button
              while the accept is in flight, and goes when the accept lands. */}
          <VendorTermsModal
            show={readingTerms}
            onHide={closeTerms}
            version={termsStanding.version}
            onAccept={() => void acceptTermsAndReturn(termsStanding.version)}
            onShown={reportSeen}
          />
        </>
      )}
      {items.length > 0 && (
        <AlertBanner variant="warning" title="Complete your store setup">
          Add your{' '}
          {items.map((item, i) => (
            <span key={item.label}>
              {i > 0 && (i === items.length - 1 ? ' and ' : ', ')}
              {'path' in item ? (
                <Link to={item.path}>{item.label}</Link>
              ) : (
                <button type="button" onClick={item.onClick} disabled={startingStripe}>
                  {item.label}
                </button>
              )}
            </span>
          ))}{' '}
          to start accepting rentals.
        </AlertBanner>
      )}
    </>
  );
}
